import * as React from "react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, ChevronRight, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

export interface SearchableDropdownOption {
    id: string;
    label: string;
    icon?: React.ElementType;
    color?: string;
    bgColor?: string;
    type?: string;
    typeLabel?: string;
    typeIcon?: React.ElementType;
    typeColor?: string;
    typeBgColor?: string;
    subOptions?: Array<{
        id: string;
        label: string;
        icon?: React.ElementType;
        color?: string;
        subOptions?: Array<{
            id: string;
            label: string;
            icon?: React.ElementType;
            color?: string;
        }>;
    }>;
}

interface SearchableDropdownProps {
    options: SearchableDropdownOption[];
    value?: string;
    subValue?: string;
    onSelect: (value: string | undefined, subValue?: string) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    emptyText?: string;
    loading?: boolean;
    width?: string;
    className?: string;
}

export function SearchableDropdown({
    options,
    value,
    subValue,
    onSelect,
    placeholder = "Select...",
    searchPlaceholder = "Search...",
    emptyText = "No results found.",
    loading = false,
    width = "250px",
    className
}: SearchableDropdownProps) {
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const [contentHeight, setContentHeight] = React.useState(0);
    const contentRef = React.useRef<HTMLDivElement>(null);

    // Find selected options in the nested structure
    const findSelectedOptions = () => {
        // Find the type group (first level)
        const typeGroup = options.find(opt => {
            // Direct match at type level
            if (opt.id === value) return true;
            // Check if value is in subOptions (rubrics)
            return opt.subOptions?.some(rubric => {
                if (rubric.id === value) return true;
                // If we have a subValue, check if it's in the rubric's subOptions
                return subValue && rubric.subOptions?.some(keypath => 
                    rubric.id === value && keypath.id === subValue
                );
            });
        });

        if (!typeGroup) return { typeGroup: undefined, rubric: undefined, keypath: undefined };

        // Find the selected rubric
        const rubric = typeGroup.subOptions?.find(r => {
            if (r.id === value) return true;
            return r.subOptions?.some(k => r.id === value && k.id === subValue);
        });

        // Find the selected keypath
        const keypath = rubric?.subOptions?.find(k => k.id === subValue);

        return { typeGroup, rubric, keypath };
    };

    const { typeGroup, rubric, keypath } = findSelectedOptions();

    const filteredOptions = React.useMemo(() => {
        if (!search) return options;
        
        return options.filter(option => {
            // Match on type group name
            const matchesType = option.label.toLowerCase().includes(search.toLowerCase());
            
            // Match on rubric names
            const matchesRubric = option.subOptions?.some(rubric => 
                rubric.label.toLowerCase().includes(search.toLowerCase())
            );
            
            // Match on keypath names
            const matchesKeypath = option.subOptions?.some(rubric => 
                rubric.subOptions?.some(keypath => 
                    keypath.label.toLowerCase().includes(search.toLowerCase())
                )
            );
            
            return matchesType || matchesRubric || matchesKeypath;
        });
    }, [options, search]);

    React.useEffect(() => {
        if (open && contentRef.current) {
            const height = contentRef.current.scrollHeight;
            setContentHeight(height);
        }
    }, [open, filteredOptions, search]);

    const shouldScroll = contentHeight > 300; // 300px is our max height before scrolling

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                        `group relative w-[${width}] justify-between transition-all duration-200`,
                        "h-9 hover:border-primary/50 hover:bg-accent focus:ring-1 focus:ring-primary/20",
                        className
                    )}
                    disabled={loading}
                >
                    <AnimatePresence mode="wait">
                        {loading ? (
                            <motion.div
                                initial={{ opacity: 0, y: 5 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -5 }}
                                className="flex items-center gap-2"
                            >
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span>Loading...</span>
                            </motion.div>
                        ) : rubric ? (
                            <motion.div
                                initial={{ opacity: 0, x: -5 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 5 }}
                                className="flex items-center gap-2 truncate"
                            >
                                {typeGroup?.typeIcon && (
                                    <typeGroup.typeIcon 
                                        className={cn("h-4 w-4 transition-transform group-hover:scale-110", typeGroup.typeColor)} 
                                    />
                                )}
                                <span className="font-medium">{rubric.label}</span>
                                {keypath && (
                                    <>
                                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" />
                                        <span className="text-muted-foreground truncate text-sm">
                                            {keypath.label}
                                        </span>
                                    </>
                                )}
                            </motion.div>
                        ) : (
                            <motion.span
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="text-muted-foreground"
                            >
                                {placeholder}
                            </motion.span>
                        )}
                    </AnimatePresence>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50 transition-transform group-hover:opacity-100 group-hover:scale-110" />
                </Button>
            </PopoverTrigger>
            <PopoverContent 
                className={cn(
                    `w-[${width}] p-0 shadow-lg backdrop-blur-sm`,
                    "border-primary/10 bg-background/95",
                    className
                )}
                align="start"
                sideOffset={4}
            >
                <Command shouldFilter={false} className="overflow-hidden rounded-lg">
                    <div className="border-b">
                        <CommandInput 
                            placeholder={searchPlaceholder} 
                            value={search}
                            onValueChange={setSearch}
                            className="h-9 border-0 focus:ring-0 focus-visible:ring-0"
                        />
                    </div>
                    <CommandEmpty className="py-4 text-sm text-center text-muted-foreground/80">
                        {emptyText}
                    </CommandEmpty>
                    <div 
                        ref={contentRef}
                        className={cn(
                            "relative pb-2",
                            shouldScroll ? "overflow-y-auto" : "overflow-visible",
                            shouldScroll && "max-h-[300px] scrollbar-thin scrollbar-thumb-primary/10 hover:scrollbar-thumb-primary/20 scrollbar-track-transparent"
                        )}
                    >
                        {filteredOptions.map((typeOption) => (
                            <CommandGroup
                                key={typeOption.id}
                                heading={
                                    <div className={cn(
                                        "flex items-center gap-2 px-2 py-1 rounded-md w-fit",
                                        "transition-colors duration-200",
                                        typeOption.typeBgColor
                                    )}>
                                        {typeOption.typeIcon && (
                                            <typeOption.typeIcon 
                                                className={cn("h-3.5 w-3.5", typeOption.typeColor)} 
                                            />
                                        )}
                                        <span className={cn("text-xs font-medium", typeOption.typeColor)}>
                                            {typeOption.label}
                                        </span>
                                    </div>
                                }
                                className="pt-2 last:pb-1"
                            >
                                {/* Render rubrics as sub-options */}
                                {typeOption.subOptions?.map((rubricOption) => (
                                    <React.Fragment key={rubricOption.id}>
                                        <CommandItem
                                            value={rubricOption.id}
                                            onSelect={(currentValue) => {
                                                const isDeselecting = currentValue === value && !subValue;
                                                onSelect(isDeselecting ? undefined : currentValue, undefined);
                                                if (isDeselecting) setOpen(false);
                                            }}
                                            className={cn(
                                                "flex items-center gap-2 transition-colors duration-200",
                                                "aria-selected:bg-accent/80 hover:!bg-accent/60",
                                                "text-sm text-muted-foreground hover:text-foreground"
                                            )}
                                        >
                                            <div className="flex items-center gap-2 flex-1">
                                                {rubricOption.icon && (
                                                    <rubricOption.icon 
                                                        className={cn("h-4 w-4 transition-transform", rubricOption.color)} 
                                                    />
                                                )}
                                                <span className={cn(
                                                    "transition-colors duration-200",
                                                    value === rubricOption.id && !subValue ? "text-foreground" : "text-muted-foreground"
                                                )}>
                                                    {rubricOption.label}
                                                </span>
                                                <AnimatePresence>
                                                    {value === rubricOption.id && !subValue && (
                                                        <motion.div
                                                            initial={{ scale: 0 }}
                                                            animate={{ scale: 1 }}
                                                            exit={{ scale: 0 }}
                                                            className="ml-auto"
                                                        >
                                                            <Check className="h-4 w-4" />
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>
                                        </CommandItem>

                                        {/* Render keypaths as nested sub-options */}
                                        {rubricOption.subOptions && rubricOption.subOptions.length > 0 && (
                                            <div className="relative pl-6 ml-2">
                                                <div className="absolute left-2 top-0 bottom-0 w-[1.5px] bg-muted-foreground/20" />
                                                {rubricOption.subOptions.map((keypathOption, index) => (
                                                    <CommandItem
                                                        key={`${rubricOption.id}-${keypathOption.id}`}
                                                        value={`${rubricOption.id}-${keypathOption.id}`}
                                                        onSelect={() => {
                                                            const isDeselecting = 
                                                                value === rubricOption.id && 
                                                                subValue === keypathOption.id;
                                                                
                                                            onSelect(
                                                                isDeselecting ? undefined : rubricOption.id,
                                                                isDeselecting ? undefined : keypathOption.id
                                                            );
                                                            if (isDeselecting) setOpen(false);
                                                        }}
                                                        className={cn(
                                                            "py-1.5 pl-2 transition-colors duration-200",
                                                            "aria-selected:bg-accent/80 hover:!bg-accent/60",
                                                            "text-sm text-muted-foreground hover:text-foreground",
                                                            index !== (rubricOption.subOptions?.length ?? 0) - 1 && "mb-0.5"
                                                        )}
                                                    >
                                                        <div className="flex items-center gap-2 flex-1">
                                                            {keypathOption.icon && (
                                                                <keypathOption.icon 
                                                                    className={cn("h-3.5 w-3.5 transition-transform", keypathOption.color)} 
                                                                />
                                                            )}
                                                            <span className={cn(
                                                                "transition-colors duration-200",
                                                                value === rubricOption.id && subValue === keypathOption.id ? "text-foreground" : "text-muted-foreground"
                                                            )}>
                                                                {keypathOption.label}
                                                            </span>
                                                            <AnimatePresence>
                                                                {value === rubricOption.id && subValue === keypathOption.id && (
                                                                    <motion.div
                                                                        initial={{ scale: 0 }}
                                                                        animate={{ scale: 1 }}
                                                                        exit={{ scale: 0 }}
                                                                        className="ml-auto"
                                                                    >
                                                                        <Check className="h-3.5 w-3.5" />
                                                                    </motion.div>
                                                                )}
                                                            </AnimatePresence>
                                                        </div>
                                                    </CommandItem>
                                                ))}
                                            </div>
                                        )}
                                    </React.Fragment>
                                ))}
                            </CommandGroup>
                        ))}
                    </div>
                </Command>
            </PopoverContent>
        </Popover>
    );
} 